import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type {
    ResolvedColumn,
    ResolvedFilter,
    TableSettings,
} from '../../domain';
import { storableFilters } from '../../domain/query/locked-filters';
import {
    parseFilters,
    parseSort,
    serializeFilters,
    serializeSort,
} from '../../domain/query/url-params';
import type { AdapterSnapshot, TableAdapter } from '../../ports';

export interface UseQueryPersistenceArgs<T> {
    adapter: TableAdapter<T>;
    columns: ResolvedColumn<T>[];
    snapshot: AdapterSnapshot<T>;
    settings: TableSettings;
    userFilters?: MutableRefObject<ResolvedFilter[]>;
}

function queryKey(
    filters: string | undefined,
    sort: string | undefined,
    search: string | undefined,
): string {
    return `${filters ?? ''}|${sort ?? ''}|${search ?? ''}`;
}

export function useQueryPersistence<T>(args: UseQueryPersistenceArgs<T>): void {
    const { adapter, columns, snapshot, settings, userFilters } = args;
    const hydrated = useRef<TableAdapter<T> | null>(null);
    const lastWritten = useRef<string | null>(null);

    if (hydrated.current !== adapter) {
        hydrated.current = adapter;
        lastWritten.current = null;
        const stored = settings.layout;

        if (stored.filters) {
            const filters = parseFilters(stored.filters, columns);
            if (filters.length > 0) adapter.setFilters(filters);
        }

        if (stored.sort) {
            const sort = parseSort(stored.sort, columns);
            if (sort && (adapter.canSortBy?.(sort.key) ?? true))
                adapter.setSort(sort);
        }

        if (stored.search) adapter.setSearch(stored.search);
    }

    if (lastWritten.current === null) {
        lastWritten.current = queryKey(
            settings.layout.filters,
            settings.layout.sort,
            settings.layout.search,
        );
    }

    useEffect(() => {
        const filtersToStore = storableFilters(
            userFilters?.current,
            snapshot.filters,
        );
        const filters =
            filtersToStore.length > 0
                ? serializeFilters(filtersToStore)
                : undefined;
        const sort = snapshot.sort ? serializeSort(snapshot.sort) : undefined;
        const search = snapshot.search || undefined;

        const serialized = queryKey(filters, sort, search);
        if (serialized === lastWritten.current) return;
        lastWritten.current = serialized;

        settings.setQuery({ filters, sort, search });
    }, [
        settings,
        snapshot.filters,
        snapshot.sort,
        snapshot.search,
        userFilters,
    ]);
}
